import type { ClientStructure } from '../types/api';
import { getReadinessLevelFromClaim, getReadinessPercent, type ReadinessLevel } from './readiness';

// Pure derivations over the agent's client list (one bounded fetch — see
// agentClientsStore). Everything here is in-memory math: no Firestore reads.

export const INACTIVE_AFTER_DAYS = 30;
const INACTIVE_AFTER_MS = INACTIVE_AFTER_DAYS * 24 * 60 * 60 * 1000;

export interface AgentStats {
  total: number;
  /** Mean readiness over clients with a progress snapshot; null if none. */
  avgReadinessPercent: number | null;
  /** Band 'risk' or 'none'. */
  atRiskCount: number;
  /** No checklist activity in INACTIVE_AFTER_DAYS days. */
  inactiveCount: number;
  /** Sum of claim.reportCount — 0 until the backend denormalization lands. */
  reportsSentTotal: number;
  bandCounts: Record<ReadinessLevel, number>;
}

export function lastActivityMs(client: ClientStructure): number | null {
  const snap = client.claim.progressSnapshot;
  const ts = snap?.updatedAt ?? client.claim.updatedAt;
  return ts ? ts.toMillis() : null;
}

export function computeAgentStats(clients: ClientStructure[], nowMs: number): AgentStats {
  const bandCounts: Record<ReadinessLevel, number> = {
    none: 0,
    risk: 0,
    partial: 0,
    progress: 0,
  };
  let pctSum = 0;
  let pctCount = 0;
  let atRiskCount = 0;
  let inactiveCount = 0;
  let reportsSentTotal = 0;

  for (const client of clients) {
    const band = getReadinessLevelFromClaim(client.claim);
    bandCounts[band] += 1;
    if (band === 'risk' || band === 'none') atRiskCount += 1;

    const pct = getReadinessPercent(client.claim);
    if (pct !== null) {
      pctSum += pct;
      pctCount += 1;
    }

    const activity = lastActivityMs(client);
    if (activity === null || nowMs - activity > INACTIVE_AFTER_MS) inactiveCount += 1;

    reportsSentTotal += client.claim.reportCount ?? 0;
  }

  return {
    total: clients.length,
    avgReadinessPercent: pctCount > 0 ? Math.round(pctSum / pctCount) : null,
    atRiskCount,
    inactiveCount,
    reportsSentTotal,
    bandCounts,
  };
}

export interface ClientFilters {
  search?: string;
  band?: ReadinessLevel | 'all';
}

export function filterClients(
  clients: ClientStructure[],
  { search, band }: ClientFilters
): ClientStructure[] {
  let out = clients;
  if (band && band !== 'all') {
    out = out.filter((c) => getReadinessLevelFromClaim(c.claim) === band);
  }
  const q = search?.trim().toLowerCase();
  if (q) {
    out = out.filter((c) => {
      const s = c.structure;
      return [s.address, s.city, s.state, s.zip]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q));
    });
  }
  return out;
}

export type ClientSortKey = 'address' | 'readiness' | 'checklist' | 'lastActivity' | 'lastReport';

export function sortClients(
  clients: ClientStructure[],
  key: ClientSortKey,
  direction: 'asc' | 'desc'
): ClientStructure[] {
  const dir = direction === 'asc' ? 1 : -1;
  const value = (c: ClientStructure): string | number => {
    switch (key) {
      case 'address':
        return (c.structure.address ?? '').toLowerCase();
      case 'readiness':
        return getReadinessPercent(c.claim) ?? -1;
      case 'checklist':
        return c.claim.progressSnapshot?.completed ?? -1;
      case 'lastActivity':
        return lastActivityMs(c) ?? 0;
      case 'lastReport':
        return c.claim.lastReport?.at?.toMillis() ?? 0;
    }
  };
  return [...clients].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    // Equal values (seeded claims share timestamps to the millisecond) fall
    // back to the structure id, so the order doesn't depend on query order.
    return a.structure.id < b.structure.id ? -1 : a.structure.id > b.structure.id ? 1 : 0;
  });
}

export interface RecentActivityEntry {
  structureId: string;
  address: string;
  completed: number;
  total: number;
  updatedAtMs: number;
}

export function recentActivity(clients: ClientStructure[], limit = 5): RecentActivityEntry[] {
  return (
    clients
      .flatMap((c) => {
        const snap = c.claim.progressSnapshot;
        if (!snap || !snap.updatedAt) return [];
        return [
          {
            structureId: c.structure.id,
            address: c.structure.address ?? 'Unknown address',
            completed: snap.completed,
            total: snap.total,
            updatedAtMs: snap.updatedAt.toMillis(),
          },
        ];
      })
      // Ties break on structure id: 7797 Cornucopia Lane and a seeded fixture
      // share a progress timestamp to the millisecond, and the list swapped
      // order between runs (Sept 16 2026).
      .sort(
        (a, b) =>
          b.updatedAtMs - a.updatedAtMs ||
          (a.structureId < b.structureId ? -1 : a.structureId > b.structureId ? 1 : 0)
      )
      .slice(0, limit)
  );
}
